import { useMemo, useCallback } from "react";
import { useFirestoreDoc } from "./useFirestoreDoc";
import { userPublicRef } from "../lib/data/settings";
import { canUseFeature as canUse, quotaFor as quota } from "../lib/tier-config";

/**
 * useGroupTier — inherited-benefits read path for the CLIENT.
 *
 * Premium automation on a poll/group is conferred by the OWNER's (creatorId's)
 * effective tier, not the acting user's. Rules can't read another user's private
 * users/{uid}, so the public mirror usersPublic/{creatorId}.subscription.effectiveTier
 * is the source. This hook reads that mirror live (so graceful-downgrade is correct)
 * and exposes canUseFeature/quotaFor resolved against the OWNER's effectiveTier.
 *
 * Caching: the docRef is memoized by creatorId, so useFirestoreDoc keys its snapshot
 * listener per owner and avoids re-subscribing / N+1 across re-renders.
 *
 * @param {string|null} creatorId
 * @returns {{ loading:boolean, effectiveTier:string,
 *             canUseFeature:(id:string)=>boolean, quotaFor:(key:string)=>number|null }}
 */
export function useGroupTier(creatorId) {
  const ref = useMemo(() => userPublicRef(creatorId || null), [creatorId]);
  const { data, loading } = useFirestoreDoc(ref);

  const effectiveTier = data?.subscription?.effectiveTier || "free";

  const canUseFeature = useCallback(
    (featureId) => canUse(effectiveTier, featureId),
    [effectiveTier]
  );
  const quotaFor = useCallback((key) => quota(effectiveTier, key), [effectiveTier]);

  return { loading: Boolean(loading), effectiveTier, canUseFeature, quotaFor };
}
